import { DestroyRef, inject, Injectable } from '@angular/core';
import { ResetPasswordRequest } from '../../../../../models/users/requests/reset-password-request';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { ToastService } from '../../../../../services/toast-service';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, shareReplay } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { emailValidator } from '../../../../../validators/email.validator';
import { fieldMatchValidator } from '../../../../../validators/field-match.validator';
import { passwordComplexityValidator } from '../../../../../validators/password-complexity.validator';
import { HttpStatusCode } from '@angular/common/http';
import { CustomError } from '../../../../../models/shared/custom-error';
import { Routes } from '../../../../../../routes/routes';

@Injectable()
export class ResetPasswordViewModel {
  private userDomainModel = inject(UserDomainModel);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private resettingPassword = $localize`Resetting Password...`;
  private resetSuccessTitle = $localize`Success!`;
  private resetSuccessMessage = $localize`Please sign-in with your new password.`;

  private _loadingOpts = new BehaviorSubject<LoadingOptions>(LoadingOptions.default());
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;

  private _preFillEmail = new BehaviorSubject<string>('');
  public readonly preFillEmail$ = this._preFillEmail as Observable<string>;

  private _preFillCode = new BehaviorSubject<string>('');
  public readonly preFillCode$ = this._preFillCode as Observable<string>;

  public formGroup$ = combineLatest([this.preFillEmail$, this.preFillCode$]).pipe(
    map(
      ([email, code]) =>
        new FormGroup(
          {
            email: new FormControl<string>(
              { value: email, disabled: true },
              {
                nonNullable: true,
                validators: [Validators.required, emailValidator]
              }
            ),
            code: new FormControl<string>(code, {
              nonNullable: true,
              validators: [Validators.required]
            }),
            newPassword: new FormControl<string>('', {
              nonNullable: true,
              validators: [Validators.required, passwordComplexityValidator]
            }),
            confirmPassword: new FormControl<string>('', {
              nonNullable: true,
              validators: [Validators.required]
            })
          },
          { validators: fieldMatchValidator('newPassword', 'confirmPassword') }
        )
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  private listenToQueryParams = this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
    next: params => {
      const email = decodeURIComponent(decodeURIComponent(params?.Email || params?.email || ''));
      const password = decodeURIComponent(decodeURIComponent(params?.Password || params?.password || ''));
      const code = decodeURIComponent(decodeURIComponent(params?.Code || params?.code || ''));
      if (!!email) this._preFillEmail.next(email);
      if (!!code) this._preFillCode.next(code);
      if (!!email || !!code) {
        this.router?.navigate([], { queryParams: {}, replaceUrl: true, relativeTo: this.route }).then();
      }
    }
  });

  public resetPassword(req: ResetPasswordRequest, portalType: PortalType): void {
    this._loadingOpts.addRequest(this.resettingPassword);
    this.userDomainModel.resetPassword(req, portalType).subscribe({
      next: res => {
        this._loadingOpts.removeRequest(this.resettingPassword);
        this.router.navigate(['/auth/sign-in']).then();
        this.toastService.publishSuccessMessage(this.resetSuccessTitle, this.resetSuccessMessage);
      },
      error: (error: CustomError) => {
        if (error?.code === HttpStatusCode.Locked) {
          this.router.navigate([Routes.accessRestricted()], { replaceUrl: true }).then(() => {});
        } else {
          this.toastService.publishError(error);
        }
        this._loadingOpts.removeRequest(this.resettingPassword);
      }
    });
  }
}
